import React, { useState } from 'react'
import { Button, Dropdown, Space, theme, Typography } from 'antd'
import { BarsOutlined, CheckOutlined } from '@ant-design/icons'
import { getCategoryById } from '../../../../../../../store/apis/utils/selectors/question.api.selectors'
import PatternIcon from '../../../../../../../components/common/PatternIcon'
import { useParams } from 'react-router-dom'
import { useRefinementFlow } from '../hooks/useRefinementFlow'

const { Text } = Typography

const OtherCategorySelector = ({ 
  refinementItem, 
  questions, 
  isDisabled,
  selectedCategoryId,
  hasChosen = false
}) => {
  const {layerId: questionId} = useParams()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { handleCategorySelect } = useRefinementFlow()
  const { token: themeToken } = theme.useToken()

  // Find the current question that corresponds to this refinement item
  const currentQuestion = questions?.find(question => 
    question.id === Number(questionId)
  )
  
  // Get all categories from the current question
  const allCategories = currentQuestion?.categories || []
  
  // Get displayed category IDs from refinementItem
  const displayedCategoryIds = [
    refinementItem?.categories?.[0]?.id,
    refinementItem?.categories?.[1]?.id
  ].filter(Boolean)
  
  // Filter out the categories that are already displayed
  const otherCategories = allCategories.filter(
    category => !displayedCategoryIds.includes(category.id)
  )

  const onCategorySelect = (categoryId) => {
    if (isDisabled) return
    
    handleCategorySelect(refinementItem.id, categoryId)
    setDropdownOpen(false)
  }

  // Generate menu items for the dropdown
  const menuItems = otherCategories.map(category => {
    const categoryDetails = getCategoryById(questions, category.id)
    const isSelected = selectedCategoryId === category.id
    
    return {
      key: category.id,
      label: (
        <Space>
          {categoryDetails && (
            <PatternIcon
              id={category.id}
              colorId={categoryDetails?.colorId}
              content={categoryDetails?.grade}
              type='pattern'
              color={categoryDetails?.color}
              suffix='pt'
              style={{ marginRight: '8px' }}
            />
          )}
          <Text>{categoryDetails?.name}</Text>
          {isSelected && <CheckOutlined style={{ color: themeToken.colorSuccess }} />}
        </Space>
      ),
      title: categoryDetails?.description,
      style: {
        backgroundColor: isSelected ? themeToken.colorSuccessBg : undefined,
        borderRadius: '4px',
        marginBottom: '4px',
        maxWidth: '360px'
      }
    }
  })

  const dropdownMenu = {
    items: menuItems,
    onClick: ({key}) => onCategorySelect(parseInt(key, 10)),
  }

  // Get the selected category details to display
  const selectedCategoryDetails = hasChosen && selectedCategoryId 
    ? getCategoryById(questions, selectedCategoryId)
    : null

  return (
    <Dropdown
      menu={dropdownMenu}
      onOpenChange={setDropdownOpen}
      open={dropdownOpen}
      disabled={isDisabled || otherCategories.length === 0}
      trigger={['click']} 
    >
      <Button
        type={hasChosen ? 'primary' : 'text'}
        icon={selectedCategoryDetails ? (
          <PatternIcon
            id={selectedCategoryId}
            colorId={selectedCategoryDetails?.colorId}
            content={selectedCategoryDetails?.grade}
            type='pattern'
            color={selectedCategoryDetails?.color}
            suffix='pt'
          />
        ) : <BarsOutlined />}
        disabled={isDisabled || otherCategories.length === 0}
        style={{
          backgroundColor: hasChosen ? themeToken.colorSuccessBg : undefined,
          borderColor: hasChosen ? themeToken.colorSuccess : undefined,
          color: hasChosen ? themeToken.colorSuccess : undefined,
          maxWidth: '100%',
          height: 'auto',
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          textAlign: 'left',
          padding: '4px 8px',
        }}
        onClick={(e) => e.preventDefault()}
      >
        {hasChosen && selectedCategoryDetails 
          ? selectedCategoryDetails.name 
          : hasChosen 
            ? 'Chosen other' 
            : 'Choose other'
        }
      </Button>
    </Dropdown>
  )
}

export default OtherCategorySelector 